// Follow-ups on an item: small to-dos with a due date, tick when done.
import { useState } from 'react';
import { write } from '../../lib/api';
import { fmtDateTime } from '../../lib/format';
import { Button, errText, useToast } from '../kit';
import { dueText, orgShort, personName, type Org, type Person } from './shared';

export type FollowUp = {
  id: string;
  title: string;
  due_on: string | null;
  assignee_id: string | null;
  onus_org_id: string | null;
  done_at: string | null;
  done_by: string | null;
  created_by: string | null;
  created_at: string;
  deleted_at: string | null;
};

export function FollowUps({
  itemId,
  list,
  people,
  staff,
  orgs,
  canAdd,
  canEditAll,
  me,
  onChanged,
}: {
  itemId: string;
  list: FollowUp[];
  people: Map<string, Person>;
  staff: Person[];
  orgs: Map<string, Org>;
  canAdd: boolean;
  canEditAll: boolean;
  me: string | undefined;
  onChanged: () => void;
}) {
  const toast = useToast();
  const [adding, setAdding] = useState(false);
  const [title, setTitle] = useState('');
  const [due, setDue] = useState('');
  const [who, setWho] = useState('');
  const [showDone, setShowDone] = useState(false);
  const live = list.filter((f) => !f.deleted_at);
  const open = live.filter((f) => !f.done_at).sort((a, b) => (a.due_on ?? '9999').localeCompare(b.due_on ?? '9999'));
  const done = live.filter((f) => f.done_at).sort((a, b) => (b.done_at ?? '').localeCompare(a.done_at ?? ''));

  async function add() {
    if (!title.trim()) return;
    try {
      await write('follow_ups', 'insert', { values: { item_id: itemId, title: title.trim(), due_on: due || null, assignee_id: who || null }, summary: `Added follow-up: ${title.trim().slice(0, 80)}` });
      setTitle('');
      setDue('');
      setWho('');
      setAdding(false);
      onChanged();
    } catch (e) {
      toast({ tone: 'error', text: errText(e) });
    }
  }
  async function tick(f: FollowUp, v: boolean) {
    try {
      await write('follow_ups', 'update', { id: f.id, values: { done_at: v ? new Date().toISOString() : null }, summary: `${v ? 'Done' : 'Reopened'}: ${f.title.slice(0, 80)}` });
      onChanged();
    } catch (e) {
      toast({ tone: 'error', text: errText(e) });
    }
  }
  const mayTick = (f: FollowUp) => canEditAll || f.created_by === me || f.assignee_id === me;

  const row = (f: FollowUp) => {
    const d = f.done_at ? null : dueText(f.due_on);
    return (
      <div className={`fu ${f.done_at ? 'done' : ''}`} key={f.id}>
        <input type="checkbox" checked={Boolean(f.done_at)} disabled={!mayTick(f)} aria-label={`Done: ${f.title}`} onChange={(e) => void tick(f, e.target.checked)} />
        <div className="fu-main">
          <span className="fu-title">{f.title}</span>
          <span className="muted small">
            {f.assignee_id ? personName(people.get(f.assignee_id)) : 'Anyone'}
            {f.onus_org_id ? ` · onus ${orgShort(orgs.get(f.onus_org_id))}` : ''}
            {f.done_at ? ` · done ${fmtDateTime(f.done_at)}${f.done_by ? ` by ${personName(people.get(f.done_by))}` : ''}` : ''}
          </span>
        </div>
        {d ? <span className={`due due-${d.tone}`}>{d.text}</span> : null}
      </div>
    );
  };

  return (
    <div>
      {open.length === 0 && !adding ? <p className="muted small" style={{ margin: '0 0 8px' }}>No open follow-ups.</p> : null}
      {open.map(row)}
      {done.length ? (
        <button type="button" className="linkbtn small" onClick={() => setShowDone(!showDone)}>
          {showDone ? 'Hide' : 'Show'} {done.length} done
        </button>
      ) : null}
      {showDone ? done.map(row) : null}
      {canAdd ? (
        adding ? (
          <div className="fu-form">
            <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="What needs chasing or checking" aria-label="Follow-up" autoFocus onKeyDown={(e) => e.key === 'Enter' && void add()} />
            <input type="date" value={due} onChange={(e) => setDue(e.target.value)} aria-label="Due" />
            <select value={who} onChange={(e) => setWho(e.target.value)} aria-label="Who">
              <option value="">Anyone</option>
              {staff.map((p) => (
                <option key={p.id} value={p.id}>
                  {personName(p)}
                </option>
              ))}
            </select>
            <Button kind="primary" onClick={() => void add()} disabled={!title.trim()}>
              Add
            </Button>
            <Button onClick={() => setAdding(false)}>Cancel</Button>
          </div>
        ) : (
          <Button kind="ghost" onClick={() => setAdding(true)}>
            + Add follow-up
          </Button>
        )
      ) : null}
    </div>
  );
}
